import { KNOWLEDGE_BASE_SOURCE_TYPE_LEARNED } from './folder-types'

export type AgentKnowledgeBaseDisplayDocument = {
  id: string
  folder_id?: string | null
}

export type AgentKnowledgeBaseDisplayFolder = {
  id: string
  name: string
  document_count: number
}

export type AgentKnowledgeBaseDisplayItem<
  TDocument extends AgentKnowledgeBaseDisplayDocument,
  TFolder extends AgentKnowledgeBaseDisplayFolder,
> =
  | {
      kind: 'folder'
      key: `folder:${string}`
      folder: TFolder
      documents: TDocument[]
      documentIds: string[]
      selectedDocumentCount: number
      isSelected: boolean
    }
  | {
      kind: 'document'
      key: `document:${string}`
      document: TDocument
      documentIds: [string]
    }

export function buildAgentKnowledgeBaseDisplayItems<
  TDocument extends AgentKnowledgeBaseDisplayDocument,
  TFolder extends AgentKnowledgeBaseDisplayFolder,
>(input: {
  activeDocuments: TDocument[]
  allDocuments: TDocument[]
  folders: TFolder[]
  selectedDocumentIds: string[]
}): AgentKnowledgeBaseDisplayItem<TDocument, TFolder>[] {
  const selectedIds = new Set(input.selectedDocumentIds)
  const allDocumentsByFolder = new Map<string, TDocument[]>()
  const activeDocumentsByFolder = new Map<string, TDocument[]>()

  for (const document of input.allDocuments) {
    if (!document.folder_id) continue
    const documents = allDocumentsByFolder.get(document.folder_id) ?? []
    documents.push(document)
    allDocumentsByFolder.set(document.folder_id, documents)
  }

  for (const document of input.activeDocuments) {
    if (!document.folder_id) continue
    const documents = activeDocumentsByFolder.get(document.folder_id) ?? []
    documents.push(document)
    activeDocumentsByFolder.set(document.folder_id, documents)
  }

  const collapsedFolderIds = new Set<string>()
  const folderItems: AgentKnowledgeBaseDisplayItem<TDocument, TFolder>[] = []

  for (const folder of input.folders) {
    const allFolderDocuments = allDocumentsByFolder.get(folder.id) ?? []
    if (allFolderDocuments.length === 0) continue

    const selectedDocumentCount = allFolderDocuments.filter(document =>
      selectedIds.has(document.id)
    ).length
    const isSelected = selectedDocumentCount === allFolderDocuments.length
    if (isSelected) collapsedFolderIds.add(folder.id)

    folderItems.push({
      kind: 'folder',
      key: `folder:${folder.id}`,
      folder,
      documents: activeDocumentsByFolder.get(folder.id) ?? [],
      documentIds: allFolderDocuments.map(document => document.id),
      selectedDocumentCount,
      isSelected,
    })
  }

  const documentItems: AgentKnowledgeBaseDisplayItem<TDocument, TFolder>[] =
    input.activeDocuments
      .filter(document => !document.folder_id || !collapsedFolderIds.has(document.folder_id))
      .map(document => ({
        kind: 'document',
        key: `document:${document.id}`,
        document,
        documentIds: [document.id],
      }))

  return [...folderItems, ...documentItems]
}

/** An agent's Knowledge tab never offers a learned document: it belongs to the agent that learned it. */
export function documentsOfferedToAgents<TDocument extends { source_type?: string | null }>(
  documents: TDocument[]
): TDocument[] {
  return documents.filter(document => document.source_type !== KNOWLEDGE_BASE_SOURCE_TYPE_LEARNED)
}
